(ns frontend.ui.component.lp-portal.bar-chart.view
  (:require
   [frontend.ui.component.chart.utils :as chart-utils]
   [frontend.ui.component.chart.view :refer [chart chart-svg]]
   [frontend.ui.page.lp-portal.report.util :as util]
   [potpuri.core :as p]
   [frontend.ui.style.theme.core :as theme]
   [frontend.ui.style.theme.core :refer [color]]))

(defn ^{:jsdoc ["@nocollapse"]} point-formatter
  []
  (this-as
   js-data-point
   (let [point (js->clj js-data-point
                        :keywordize-keys
                        true)]
     (str (util/->float-1-decimals (:y point))
     ))))

(defn ^{:jsdoc ["@nocollapse"]} point-formatter-pct
  []
  (this-as
   js-data-point
   (let [point (js->clj js-data-point
                        :keywordize-keys
                        true)]
     (str (util/->float-1-decimals (:y point)) "%"))))

(defn bar-chart-config
  [labels values & {:keys [orientation] :or {orientation :bar}}]
  {:chart       {:type orientation}
   :tooltip     {:outside         true
                 :backgroundColor (theme/color [:page-bg :bg])
                 :useHtml         true
                 :style           {:opacity         1
                                   :backgroundColor (theme/color [:page-bg :bg])}
                 :enabled         false}
   :plotOptions {:series     {:colorByPoint true}
                 :dataLabels {:enabled true}}
   :colors      (util/gradient-colors [:lp-report :chart :bar :gradient-start]
                                      [:lp-report :chart :bar :gradient-end]
                                      (count values))
   :legend      {:enabled false}
   :xAxis       {:categories    labels
                 :lineColor     (theme/color [:chart :bar-chart :grid :line])
                 :gridLineColor (theme/color [:chart :bar-chart :grid :line])}
   :yAxis       {:title         false
                 :labels        {:format "{text} M€"}
                 :lineColor     (theme/color [:chart :bar-chart :grid :line])
                 :gridLineColor (theme/color [:chart :bar-chart :grid :line])}
   :series      [{:data values}]})

(defn bar-last-n
  [{:keys [labels values axis-labels last-n]}]
  (let [data (map vector labels values)
        shown-data (->> data
                        (reverse)
                        (take last-n)
                        (reverse))]
    {:xAxis       [{:categories    (map first shown-data)
                    :lineColor     (theme/color [:chart :bar-chart :grid :line])
                    :gridLineColor (theme/color [:chart :bar-chart :grid :line])}]
     :yAxis       [{:title         false
                    :labels        axis-labels
                    :lineColor     (theme/color [:chart :bar-chart :grid :line])
                    :gridLineColor (theme/color [:chart :bar-chart :grid :line])}]
     :colors      (util/gradient-colors [:lp-report :chart :bar :gradient-start]
                                        [:lp-report :chart :bar :gradient-end]
                                        last-n)
     :plotOptions {:series {:dataLabels {:enabled   true
                                         :style     {:color       "contrast"
                                                     :textOutline false}
                                         :formatter point-formatter}}}
     :legend      false
     :series      [{:type    :column
                    :data    (map second shown-data)
                    :tooltip {:outside         true
                              :useHtml         true
                              :backgroundColor (theme/color [:page-bg :bg])
                              :style           {:opacity         1
                                                :backgroundColor (theme/color [:page-bg :bg])}
                              :pointFormatter  chart-utils/pct-formatter}}]}))

(defn ^{:jsdoc ["@nocollapse"]} pct-point-formatter
  []
  (this-as
   js-data-point
   (let [point (js->clj js-data-point
                        :keywordize-keys
                        true)]
     (str "<strong>"
          (:x point)
          "</strong><br> "
          (util/->percent-1-decimal (/ (:y point) 100))))))

(defn column-top-n
  [{:keys [labels values title formatter top-n]}]
  (let [data (map vector labels values)
        shown-data (->> data
                        (sort-by second)
                        (take top-n)
                        (reverse))]
    {:xAxis       [{:categories    (map first shown-data)
                    :lineColor     (theme/color [:chart :bar-chart :grid :line])
                    :gridLineColor (theme/color [:chart :bar-chart :grid :line])
                   }]
     :yAxis       [{:title         {:text title}
                    :lineColor     (theme/color [:chart :bar-chart :grid :line])
                    :gridLineColor (theme/color [:chart :bar-chart :grid :line])
                   }]
     :colors      (util/gradient-colors [:lp-report :chart :column :gradient-start]
                                        [:lp-report :chart :column :gradient-end]
                                        top-n)
     :tooltip     {:outside         true
                   :useHtml         true
                   :backgroundColor (theme/color [:page-bg :bg])
                   :style           {:opacity         1
                                     :backgroundColor (theme/color [:page-bg :bg])}
                   :formatter       pct-point-formatter}
     :plotOptions {:series {:dataLabels {:enabled   true
                                         :style     {:color       "contrast"
                                                     :textOutline false}
                                         :formatter (or formatter point-formatter)}}}
     :legend      false
     :series      [{:type :bar
                    :data (map second shown-data)}]}))

(defn waterfall-chart-config
  [labels values]
  {:chart       {:type "waterfall"}
   :tooltip     {:outside         true
                 :useHtml         true
                 :backgroundColor (theme/color [:page-bg :bg])
                 :style           {:opacity         1
                                   :backgroundColor (theme/color [:page-bg :bg])}
                 :enabled         false}
   :plotOptions {:series     {:colorByPoint true}
                 :waterfall  {}
                 :dataLabels {:enabled true}}
   :colors      (util/gradient-colors [:lp-report :chart :waterfall :gradient-start]
                                      [:lp-report :chart :waterfall :gradient-end]
                                      (count values))
   :legend      {:enabled false}
   :xAxis       {:type          "category"
                 :categories    labels
                 :lineColor     (theme/color [:chart :bar-chart :grid :line])
                 :gridLineColor (theme/color [:chart :bar-chart :grid :line])}
   :yAxis       {:visible       true
                 :title         false
                 :labels        {:format "{text} M€"}
                 :softMin       0
                 :softMax       0
                 :lineColor     (theme/color [:chart :bar-chart :grid :line])
                 :gridLineColor (theme/color [:chart :bar-chart :grid :line])}
   :series      [{:data (->> labels
                             (map-indexed (fn [idx lbl]
                                            (merge {:name lbl
                                                    :y    (get values idx)}
                                                   (when (= (inc idx) (count labels))
                                                     {:isSum true}))))
                             (doall))}]})

(defn bar-chart
  [labels values & {:keys [orientation] :or {orientation :bar}}]
  [chart (bar-chart-config labels values :orientation orientation)])

(defn bar-chart-svg
  [labels values & {:keys [orientation] :or {orientation :bar}}]
  (chart-svg "highcharts" (bar-chart-config labels values :orientation orientation)))

(defn column-chart
  [labels values]
  [bar-chart labels values :orientation :column])

(defn column-chart-svg
  [labels values]
  (bar-chart-svg labels values :orientation :column))

(defn multi-bar-cfg
  ;; TODO -> y axis scale to ME
  [{:keys [labels series plotOptions]} & {:keys [legend?] :or {legend? true}}]
  (merge
   {:xAxis       [{:categories    labels
                   :lineColor     (theme/color [:chart :bar-chart :grid :line])
                   :gridLineColor (theme/color [:chart :bar-chart :grid :line])}]
    :yAxis       {:title         :nil
                  :lineColor     (theme/color [:chart :bar-chart :grid :line])
                  :gridLineColor (theme/color [:chart :bar-chart :grid :line])}
    :legend      {:enabled legend?}
    ;;               :labels {:format "{value}"}}
    :colors      (util/gradient-colors [:lp-report :chart :multi-bar :gradient-start]
                                       [:lp-report :chart :multi-bar :gradient-end]
                                       (count series))
    :tooltip     {:outside         true
                  :useHtml         true
                  :backgroundColor (theme/color [:page-bg :bg])
                  :style           {:opacity         1
                                    :backgroundColor (theme/color [:page-bg :bg])}
                  :shared          true}
    :series      series
    :plotOptions (p/deep-merge {:line {:marker {:enabled false}}}
                               plotOptions)}))

(defn multi-bar-chart
  [{:keys [labels series] :as data} & {:keys [legend?] :or {legend? true} :as opts}]
  [chart
   (multi-bar-cfg data opts)])
